import React, { useState, useEffect } from "react";
import PlayerProgress from "./PlayerProgress";
import GoogleAd from "./GoogleAd";
import preguntas from "../data/preguntas330.json";
import temas from "../data/temas_dgt";
import { ref, onValue, set, update } from "firebase/database";
import { db } from "../firebase";
import "./GameOnline.css";

function getTemasPendientes(jugador) {
  return temas.filter(t => (jugador.progreso?.[t] || 0) < 4);
}
function getTemasCompletados(jugador) {
  return temas.filter(t => (jugador.progreso?.[t] || 0) >= 4);
}
function getTemasCompletos10(jugador) {
  return temas.filter(t => (jugador.progreso?.[t] || 0) >= 4).length >= 10;
}

function GameOnline({ codigoSala, miNombre, esCreador, onSalir }) {
  const [sala, setSala] = useState(null);
  const [jugadores, setJugadores] = useState([]);
  const [turnoActual, setTurnoActual] = useState(0);
  const [estado, setEstado] = useState("lobby"); // lobby, ruleta, pregunta, explicacion, fin
  const [categoriaActual, setCategoriaActual] = useState(null);
  const [preguntaActual, setPreguntaActual] = useState(null);
  const [opcionSeleccionada, setOpcionSeleccionada] = useState("");
  const [bloquear, setBloquear] = useState(false);

  // --- Escuchar la sala en tiempo real ---
  useEffect(() => {
    const salaRef = ref(db, `salas/${codigoSala}`);
    const unsubscribe = onValue(salaRef, snap => {
      const data = snap.val();
      setSala(data);
      if (data && data.jugadores) {
        setJugadores(Object.values(data.jugadores));
      }
      if (data && typeof data.turno === "number") {
        setTurnoActual(data.turno);
      }
      if (data && data.estadoJuego) {
        setEstado(data.estadoJuego);
      }
      if (data && data.categoriaActual) {
        setCategoriaActual(data.categoriaActual);
      }
      if (data && data.preguntaActual) {
        setPreguntaActual(data.preguntaActual);
      } else {
        setPreguntaActual(null);
      }
      if (data && typeof data.opcionSeleccionada === "string") {
        setOpcionSeleccionada(data.opcionSeleccionada);
      } else {
        setOpcionSeleccionada("");
      }
      setBloquear(false); // Siempre desbloquea en cualquier cambio de sala
    });
    return () => unsubscribe();
  }, [codigoSala]);

  const soyCreador = sala && sala.creador === miNombre;
  const esMiTurno = jugadores[turnoActual]?.nombre === miNombre;

  // Lanzar ruleta (elige una categoría aleatoria pendiente)
  const lanzarRuleta = () => {
    const jugadorEnTurno = jugadores[turnoActual];
    const pendientes = getTemasPendientes(jugadorEnTurno);
    if (pendientes.length === 0) {
      set(ref(db, `salas/${codigoSala}/estadoJuego`), "fin");
      return;
    }
    const categoria = pendientes[Math.floor(Math.random() * pendientes.length)];
    update(ref(db, `salas/${codigoSala}`), {
      categoriaActual: categoria,
      estadoJuego: "pregunta",
    });
  };

  // Solo el jugador en turno genera la pregunta
  useEffect(() => {
    if (estado !== "pregunta" || !categoriaActual) return;
    if (!esMiTurno) return; // SOLO el jugador EN TURNO genera la pregunta
    if (preguntaActual) return; // Ya hay una pregunta
    // Buscar preguntas del tema, filtrar respondidas
    const jugador = jugadores[turnoActual];
    const respondidas = jugador?.preguntasRespondidas || [];
    const preguntasPorTema = preguntas.filter(q => q.tema === categoriaActual);
    const disponibles = preguntasPorTema.filter(
      q => !respondidas.includes(q.id)
    );
    if (disponibles.length === 0) {
      siguienteTurno();
      return;
    }
    const pregunta = disponibles[Math.floor(Math.random() * disponibles.length)];
    update(ref(db, `salas/${codigoSala}`), {
      preguntaActual: pregunta,
      opcionSeleccionada: "",
    });
    // eslint-disable-next-line
  }, [estado, categoriaActual, jugadores, turnoActual, preguntaActual, esMiTurno, codigoSala]);

  // Confirmar respuesta (solo el jugador en turno puede hacerlo)
  const confirmarRespuesta = () => {
    if (!preguntaActual || !opcionSeleccionada) return;
    setBloquear(true);
    const esCorrecta = opcionSeleccionada === preguntaActual.correcta;
    // Actualiza progreso del jugador
    const jugadorKey = Object.keys(sala.jugadores)[turnoActual];
    const jugador = sala.jugadores[jugadorKey];
    const progresoNuevo = { ...jugador.progreso };
    if (esCorrecta) {
      progresoNuevo[categoriaActual] = (progresoNuevo[categoriaActual] || 0) + 1;
    }
    const historialNuevo = [...(jugador.preguntasRespondidas || []), preguntaActual.id];
    // Actualizar en Firebase
    update(ref(db, `salas/${codigoSala}/jugadores/${jugador.nombre}`), {
      progreso: progresoNuevo,
      preguntasRespondidas: historialNuevo,
    });
    update(ref(db, `salas/${codigoSala}`), {
      estadoJuego: "explicacion",
      mensaje: esCorrecta
        ? "✅ ¡Correcto!"
        : `❌ Incorrecto. La correcta era: ${preguntaActual.opciones[preguntaActual.correcta]}`,
    });
  };

  // Avanzar turno automático tras la explicación (solo el creador lo hace)
  useEffect(() => {
    if (estado !== "explicacion") return;
    const timeout = setTimeout(() => {
      if (soyCreador) {
        siguienteTurno();
      }
    }, 2200);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line
  }, [estado, soyCreador]);

  function siguienteTurno() {
    if (!soyCreador) return;
    const totalJugadores = jugadores.length;
    let nuevoTurno = (turnoActual + 1) % totalJugadores;
    // Busca al siguiente jugador que le queden temas
    for (let i = 0; i < totalJugadores; i++) {
      const idx = (nuevoTurno + i) % totalJugadores;
      const jugador = jugadores[idx];
      if (getTemasPendientes(jugador).length > 0) {
        nuevoTurno = idx;
        break;
      }
    }
    update(ref(db, `salas/${codigoSala}`), {
      turno: nuevoTurno,
      estadoJuego: "ruleta",
      preguntaActual: null,
      categoriaActual: null,
      mensaje: "",
      opcionSeleccionada: "",
    });
  }

  // Cuando el jugador selecciona opción
  const handleOpcionSeleccionada = (op) => {
    if (!esMiTurno || estado !== "pregunta" || bloquear) return;
    update(ref(db, `salas/${codigoSala}`), {
      opcionSeleccionada: op,
    });
  };

  // --- LOBBY: Pantalla de espera hasta que el creador pulse "Empezar" ---
  if (estado === "lobby") {
    return (
      <div className="gameonline-container anim-fadein">
        <div className="salaonline-panel-lobby">
          <div className="codigo-sala-box">
            <span>Código de sala: </span>
            <span className="codigo-sala-online">{codigoSala}</span>
            <button
              className="btn-copy"
              onClick={() => {
                navigator.clipboard.writeText(codigoSala);
              }}
              title="Copiar código de sala"
            >📋</button>
          </div>
          <h2>
            <span style={{ fontSize: "1.35rem" }}>👥</span> Jugadores en la sala
          </h2>
          <ul className="lista-jugadores-lobby">
            {jugadores.map((j, idx) => (
              <li key={j.nombre}>
                {j.nombre}
                {sala?.creador === j.nombre && (
                  <span className="emoji-crown" title="Creador de la sala">👑</span>
                )}
              </li>
            ))}
          </ul>
          {soyCreador && (
            <button
              className="btn-jugaronline"
              onClick={() => {
                update(ref(db, `salas/${codigoSala}`), {
                  estadoJuego: "ruleta",
                });
              }}
            >Empezar partida</button>
          )}
          {!soyCreador && (
            <div className="espera-crear-juego">Esperando a que el creador empiece...</div>
          )}
          <button className="btn-volver" onClick={onSalir}>Salir de la sala</button>
        </div>
      </div>
    );
  }

  // --- MOSTRAR PROGRESO DE TODOS LOS JUGADORES ---
  return (
    <div className="gameonline-container anim-fadein">
      <div className="top-panel naranja">
        <span className="turno-label-naranja">
          {jugadores.length > 1
            ? `Turno de: ${jugadores[turnoActual]?.nombre || "?"}`
            : "Tu progreso"}
        </span>
        <button className="btn-volver-top" onClick={onSalir} title="Salir al inicio">
          ⬅️
        </button>
      </div>

      <div className="progreso-todos">
        {jugadores.map((j, idx) => (
          <div
            key={j.nombre}
            className={`jugador-box${idx === turnoActual ? " actual" : ""}`}
          >
            <div className="jugador-nombre">
              {j.nombre}
              {sala?.creador === j.nombre && (
                <span className="emoji-crown" title="Creador de la sala">👑</span>
              )}
              {idx === turnoActual && <span className="emoji-turno" title="Turno">💠 Turno</span>}
            </div>
            <PlayerProgress
              progreso={j.progreso}
              temas={temas}
              temasCompletados={getTemasCompletados(j)}
            />
            <div className="temas-completados-naranja">
              Temas: <b>{getTemasCompletados(j).length}</b>
            </div>
          </div>
        ))}
      </div>

      {/* Juego online, ruleta/pregunta/explicacion */}
      {estado === "ruleta" && esMiTurno && (
        <div className="ruleta-panel-naranja">
          <button className="btn-ruleta-naranja" onClick={lanzarRuleta}>
            Girar ruleta de tema
          </button>
        </div>
      )}
      {estado === "ruleta" && !esMiTurno && (
        <div className="ruleta-panel-naranja">
          Esperando a que <b>{jugadores[turnoActual]?.nombre}</b> elija el tema...
        </div>
      )}

      {/* SOLO el jugador en turno ve y responde la pregunta */}
      {estado === "pregunta" && preguntaActual && esMiTurno && (
        <div className="pregunta-card-naranja anim-fadein">
          <h3 className="pregunta-title">{preguntaActual.pregunta}</h3>
          <div className="opciones-list">
            {Object.entries(preguntaActual.opciones).map(([letra, texto]) => (
              <button
                key={letra}
                className={`opcion-btn-naranja ${opcionSeleccionada === letra ? "selected" : ""}`}
                onClick={() => handleOpcionSeleccionada(letra)}
                disabled={bloquear}
              >
                <b>{letra}.</b> {texto}
              </button>
            ))}
          </div>
          <button
            className="btn-confirmar-naranja"
            onClick={confirmarRespuesta}
            disabled={bloquear || !opcionSeleccionada}
          >
            Confirmar respuesta
          </button>
        </div>
      )}

      {/* Los demás ven solo el aviso de que se está respondiendo */}
      {estado === "pregunta" && preguntaActual && !esMiTurno && (
        <div className="pregunta-card-naranja anim-fadein" style={{ textAlign: "center" }}>
          <div style={{ fontSize: "1.2rem" }}>
            Esperando a que <b>{jugadores[turnoActual]?.nombre}</b> responda...
          </div>
        </div>
      )}

      {estado === "explicacion" && (
        <div className="explicacion-card-naranja anim-fadein">
          <div className="mensaje-txt">{sala?.mensaje}</div>
        </div>
      )}

      {estado === "fin" && (
        <div className="fin-panel-naranja anim-fadein">
          <h2>🏆 ¡Fin de la partida!</h2>
          <div>
            {jugadores
              .filter(getTemasCompletos10)
              .map(j => (
                <div key={j.nombre} className="ganador-naranja">
                  {j.nombre} ha completado 10 temas.
                </div>
              ))}
          </div>
          <button className="btn-volver" onClick={onSalir}>Volver al inicio</button>
        </div>
      )}

      <GoogleAd />
    </div>
  );
}

export default GameOnline;
